import Dropdown, { SelectItemProps } from 'core-system/Dropdown'
import FlexContainer from 'core-system/FlexContainer'
import AlertIcon from 'core-system/Icons/Misc/Alert'
import OverflowIcon from 'core-system/Icons/Misc/Overflow'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import pxToRem from 'core-system/utils/pxToRem'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import styled from 'styled-components'
import MyCommuterArchive from './MyCommuterDetails/MyCommuterArchive'
import MyCommuterDetailsTransactions from './MyCommuterDetails/MyCommuterDetailsTransactions'
import MyCommuterDetailsTrips from './MyCommuterDetails/MyCommuterDetailsTrips'
import { employeeActions } from 'redux/employee/employeeSlice'
import MyCommuterChangeWorksite from './MyCommuterDetails/MyCommuterChangeWorksite'
import { employerActions } from 'redux/employer/employerSlice'
import MyCommuterGiveOTR from './MyCommuterDetails/MyCommuterGiveOTR'
import { incentivesActions } from 'redux/incentives/incentivesSlice'
import MyCommuterInvite from './MyCommuterDetails/MyCommuterInvite'
import { getStatusChipAttributes } from './CommutersTable'
import Box from 'core-system/Box/Box'
import Tabs from 'core-system/Tabs/Tabs'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import SegmentsDrawer from '../components/SegmentsDrawer'
import Loading from 'core-system/Loading'
import Avatar from 'core-system/Avatar/Avatar'
import ColorsPill from 'core-system/Pill/ColorPill'
import CommuterAllocationAdjustment from './MyCommuterDetails/CommuterAllocationAdjustment'
import MyCommuterManageTags from './MyCommuterDetails/MyCommuterManageTags'
import MyCommuterDetailsTags from './MyCommuterDetails/MyCommuterDetailsTags'
import CommuterBasicInfo from './MyCommuterDetails/CommuterBasicInfo'
import CommuterBalancesBenefits from './MyCommuterDetails/CommuterBalancesBenefits'
import CommuterGroupsEngagement from './MyCommuterDetails/CommuterGroupsEngagement'
import CommuterAllocations from './MyCommuterDetails/CommuterAllocations'
import CommuterSupport from './MyCommuterDetails/CommuterSupport'
import CommuterReimbursements from './MyCommuterDetails/CommuterReimbursements'
import { reimbursementsActions } from 'redux/reimbursements/reimbursementsSlice'
import RequestTransitCardModal from './MyCommuterDetails/RequestTransitCardModal'
import {
  COMMUTER_MENU_IDS,
  getCommuterDetailsMenuItems,
} from './MyCommuterDetails/commuterDetailsMenuItems'
import { areAllRequestableAgenciesBlocked } from './MyCommuterDetails/transitCardUtils'

const ALLOCATIONS_TAB = 4
const TRIPS_TAB = 1
const TRANSACTIONS_TAB = 2
const REIMBURSEMENTS_TAB = 5
const SUPPORT_TAB = 6
const TAGS_TAB = 3
const DEFAULT_TAB = ALLOCATIONS_TAB

const BoxContainer = styled(Box)`
  padding: 1rem;
  margin-bottom: 1.5rem;
`

const BodyContainer = styled.div`
  margin-top: 1.5rem;
`

const HeaderContainer = styled(FlexContainer)`
  position: relative;
  width: 100%;
`

interface CommutersInfoDrawerProps {
  open: boolean
  closeModal: () => void
  employeeId: string
  upcomingAllocations?: any
}

const CommutersInfoDrawer = React.memo((props: CommutersInfoDrawerProps) => {
  const { open, closeModal, employeeId, upcomingAllocations = {} } = props

  const dispatch = useDispatch()

  const {
    employeeWorksiteChangeFlag,
    employeeUpdateFlag,
    myCommuterDetails,
    myCommuterTransactions,
    allCommutersMap,
    archivedEmployees,
    myCommuters,
    rosterPages,
    requestTransitCardStatus,
  } = useSelector((state: AppState) => state.employee)

  const { worksites, allEmployeeSegmentId, profile, segmentsMap } = useSelector(
    (state: AppState) => state.employer
  )
  const allOneTimeRewards = useSelector(
    (state: AppState) => state.incentives?.allOneTimeRewards
  )
  const {
    employeeSubmissions,
    employeeSubmissionsEmployeeId,
    employeeSubmissionsLoading,
    employeeSubmissionsError,
  } = useSelector((state: AppState) => state.reimbursements)

  // Fetch the employee's original worksite segment id
  const currEmpWorksiteSegmentId = myCommuterDetails
    ? worksites?.find(
        (worksite) => worksite.id === myCommuterDetails[employeeId]?.worksiteId
      )?.segmentId
    : null

  const { qtfPrograms } = useSelector((state: AppState) => state.qtf)
  const grhPrograms = useSelector((state: AppState) => state.grh?.grhPrograms)

  const [currentDate, setCurrentDate] = useState(moment().format('YYYY-MM'))
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  const [manageTagsReturnTab, setManageTagsReturnTab] = useState(0)
  const [selectedWorksiteId, setSelectedWorksiteId] = useState(null)
  const [statusChipAttributes, setStatusChipAttributes] = useState<{
    text: string
    color: string
  }>({
    text: 'Needs Invite',
    color: 'pink',
  })

  const products = new Set(profile.products)

  // OTR details
  const [otrAmount, setOtrAmount] = useState<number>(0)
  const [otrMemo, setOtrMemo] = useState<string>('')
  const [isRequestTransitModalOpen, setIsRequestTransitModalOpen] =
    useState(false)

  const onCloseModal = useCallback(() => {
    dispatch(employeeActions.resetMyCommuterDetails({ employeeId }))
    dispatch(employeeActions.toggleRequestTransitCardStatusOff())
    setActiveTab(DEFAULT_TAB)
    setSelectedWorksiteId(null)
    setIsRequestTransitModalOpen(false)
    closeModal()
  }, [dispatch, employeeId, closeModal])

  //after employee is successfully archived, close the modal
  useEffect(() => {
    if (employeeUpdateFlag) {
      dispatch(employeeActions.toggleEmployeeFlag())
      dispatch(employeeActions.refreshActiveRoster())
      if (myCommuters && Object.keys(myCommuters).length > 0) {
        dispatch(
          employeeActions.getMyCommuters({ segment: allEmployeeSegmentId })
        )
      }
      onCloseModal()
    }
    // myCommuters is read only to decide whether a drawer already loaded the full list.
    // onCloseModal is omitted so a new close callback does not refresh the roster again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeUpdateFlag, dispatch, allEmployeeSegmentId])

  // After the employee's worksite is changed, reload the commuter data and close the commuter details modal
  useEffect(() => {
    if (employeeWorksiteChangeFlag === 'success') {
      const newWorksiteSegmentId = worksites?.find(
        (worksite) => worksite.id === selectedWorksiteId
      )?.segmentId

      dispatch(employeeActions.refreshActiveRoster())
      const loadedLists = myCommuters || {}
      if (loadedLists[allEmployeeSegmentId]) {
        dispatch(
          employeeActions.getMyCommuters({ segment: allEmployeeSegmentId })
        )
      }
      if (currEmpWorksiteSegmentId && loadedLists[currEmpWorksiteSegmentId]) {
        dispatch(
          employeeActions.getMyCommuters({
            segment: currEmpWorksiteSegmentId,
          })
        )
      }
      if (newWorksiteSegmentId && loadedLists[newWorksiteSegmentId]) {
        dispatch(
          employeeActions.getMyCommuters({ segment: newWorksiteSegmentId })
        )
      }

      dispatch(employeeActions.toggleEmployeeWorksiteChangeFlagOff())
      onCloseModal()
    }
  }, [
    onCloseModal,
    employeeWorksiteChangeFlag,
    dispatch,
    allEmployeeSegmentId,
    currEmpWorksiteSegmentId,
    selectedWorksiteId,
    worksites,
    myCommuters,
  ])

  useEffect(() => {
    if (open && employeeId && activeTab === REIMBURSEMENTS_TAB) {
      dispatch(reimbursementsActions.getEmployeeSubmissions(employeeId))
    }
  }, [open, employeeId, activeTab, dispatch])

  useEffect(() => {
    if (employeeId) {
      const currentMoment = moment()
      dispatch(
        employeeActions.getMyCommuterDetails({
          id: employeeId,
          startDate: currentMoment.startOf('month').format(),
          endDate: currentMoment.endOf('month').format(),
        })
      )
      dispatch(
        employeeActions.getMyCommuterTransactions({
          id: employeeId,
          startDate: currentMoment.startOf('month').format(),
          endDate: currentMoment.endOf('month').format(),
        })
      )
    }
  }, [dispatch, employeeId])

  useEffect(() => {
    if (myCommuterDetails && myCommuterDetails[employeeId]) {
      setStatusChipAttributes(
        getStatusChipAttributes(myCommuterDetails[employeeId])
      )
    }
  }, [myCommuterDetails, employeeId])

  const rosterRow = (
    ['all', 'pending', 'archived', 'blocked', 'declined'] as const
  ).reduce(
    (found, tab) =>
      found ||
      rosterPages?.[tab]?.results?.find((emp) => emp.id === employeeId),
    null
  )
  const basicCommuterInfo =
    myCommuterDetails?.[employeeId] ||
    allCommutersMap?.[employeeId] ||
    rosterRow

  useEffect(() => {
    if (requestTransitCardStatus === 'success' && isRequestTransitModalOpen) {
      setIsRequestTransitModalOpen(false)
      dispatch(employeeActions.toggleRequestTransitCardStatusOff())
    }
  }, [requestTransitCardStatus, isRequestTransitModalOpen, dispatch])

  if (!employeeId) return null

  const menuCallback = (item: SelectItemProps) => {
    if (item.id === COMMUTER_MENU_IDS.ARCHIVE) {
      setActiveTab(-1)
    } else if (item.id === COMMUTER_MENU_IDS.REACTIVATE) {
      dispatch(
        employeeActions.reactivateEmployee({ employeeId: item.employee.id })
      )
    } else if (item.id === COMMUTER_MENU_IDS.CHANGE_WORKSITE) {
      setActiveTab(-2)
    } else if (item.id === COMMUTER_MENU_IDS.SEND_INVITE) {
      setActiveTab(-3)
    } else if (item.id === COMMUTER_MENU_IDS.EDIT_ALLOCATION) {
      setActiveTab(-4)
    } else if (item.id === COMMUTER_MENU_IDS.GIVE_OTR) {
      setOtrAmount(0)
      setOtrMemo('')
      setActiveTab(-5)
    } else if (item.id === COMMUTER_MENU_IDS.REQUEST_TRANSIT_CARD) {
      if (item.disabled) {
        return
      }
      dispatch(employeeActions.toggleRequestTransitCardStatusOff())
      setIsRequestTransitModalOpen(true)
    } else if (item.id === COMMUTER_MENU_IDS.MANAGE_TAGS) {
      openManageTags(TAGS_TAB)
    }
  }

  const commuterWorksite =
    basicCommuterInfo &&
    worksites.find((wrk) => wrk.id === basicCommuterInfo.worksiteId)

  const hasError =
    !basicCommuterInfo ||
    !myCommuterDetails ||
    !myCommuterDetails[employeeId] ||
    !myCommuterTransactions ||
    !myCommuterTransactions[employeeId]

  const { email: commuterEmail, fleetCards, address: commuterAddress } =
    (myCommuterDetails && myCommuterDetails[employeeId]) || {}

  const handleDateChange = (isIncrease: boolean, date: string) => {
    const currentMoment = moment(date)

    if (isIncrease) {
      const newDate = currentMoment.add(1, 'month')
      setCurrentDate(newDate.format('YYYY-MM'))
      dispatch(
        employeeActions.getMyCommuterDetails({
          id: employeeId,
          startDate: newDate.startOf('month').format(),
          endDate: newDate.endOf('month').format(),
        })
      )
      dispatch(
        employeeActions.getMyCommuterTransactions({
          id: employeeId,
          startDate: currentMoment.startOf('month').format(),
          endDate: currentMoment.endOf('month').format(),
        })
      )
    } else {
      const newDate = currentMoment.subtract(1, 'month')
      setCurrentDate(newDate.format('YYYY-MM'))
      dispatch(
        employeeActions.getMyCommuterDetails({
          id: employeeId,
          startDate: newDate.startOf('month').format(),
          endDate: newDate.endOf('month').format(),
        })
      )
      dispatch(
        employeeActions.getMyCommuterTransactions({
          id: employeeId,
          startDate: currentMoment.startOf('month').format(),
          endDate: currentMoment.endOf('month').format(),
        })
      )
    }
  }

  const handleArchiveClick = (employeeId: string) => {
    dispatch(employeeActions.archiveEmployee({ employeeId }))
  }

  const handleCloseModal = () => {
    setActiveTab(DEFAULT_TAB)
    closeModal()
  }

  const handleWorksiteChange = (employeeId: string, worksiteId: string) => {
    dispatch(
      employeeActions.changeEmployeeWorksite({
        employeeId,
        worksiteId,
      })
    )
  }

  const handleGiveOTRClick = () => {
    const otrPayload = {
      employeeId: employeeId,
      amount: otrAmount * 100,
      memo: otrMemo,
    }
    dispatch(incentivesActions.createOneTimeReward(otrPayload))
    // TODO: this is a temp fix for the current balance in the modal and the MyCommutersView table not dynamically updating to reflect OTR being added
    window.location.reload()
    handleCloseModal()
  }

  const handleRequestTransitCard = ({
    transitAgencyId,
    addFunds,
    amount,
  }: {
    transitAgencyId: string
    addFunds: boolean
    amount: number | null
  }) => {
    dispatch(
      employeeActions.requestTransitCard({
        employeeId,
        transitAgencyId,
        addFunds,
        amount: addFunds && amount ? amount * 100 : null,
      })
    )
  }

  const handleInviteEmployee = (sendWelcomeEmail: boolean) => {
    dispatch(
      employerActions.rolloutUsers({
        employees: [employeeId],
        send_welcome_email: sendWelcomeEmail,
      })
    )
    window.location.reload()
    handleCloseModal()
  }

  const openManageTags = (returnTab: number) => {
    setManageTagsReturnTab(returnTab)
    setActiveTab(-6)
  }

  const handleTagsSaveComplete = () => {
    const currentMoment = moment(currentDate)
    dispatch(
      employeeActions.getMyCommuterDetails({
        id: employeeId,
        startDate: currentMoment.startOf('month').format(),
        endDate: currentMoment.endOf('month').format(),
      })
    )
    setActiveTab(manageTagsReturnTab)
  }

  const rosterArchived = [
    ...(rosterPages?.archived?.results || []),
    ...(rosterPages?.blocked?.results || []),
  ]
  const archivedEmp = !basicCommuterInfo
    ? null
    : myCommuterDetails?.[employeeId]?.archived
      ? myCommuterDetails[employeeId]
      : basicCommuterInfo.archived
        ? basicCommuterInfo
        : (archivedEmployees &&
            [...archivedEmployees].find(
              (emp) => emp.id === basicCommuterInfo.id
            )) ||
          rosterArchived.find((emp) => emp.id === basicCommuterInfo.id)
  const isBlockedEmp = archivedEmp?.block

  const isFinchEmp =
    myCommuterDetails &&
    myCommuterDetails[basicCommuterInfo?.id]?.finchIndividualId
      ? true
      : false

  const getSelectItems = () => {
    const details = myCommuterDetails?.[employeeId]
    return getCommuterDetailsMenuItems({
      isArchived: Boolean(archivedEmp),
      isFinchEmp,
      hasRegistered: Boolean(details?.hasRegistered),
      hasUpcomingAllocation: Boolean(upcomingAllocations[employeeId]),
      hasOtr: products.has('OTR'),
      canRequestTransitCard: Boolean(details?.canRequestTransitCard),
      hasNoAvailableTransitAgencies: areAllRequestableAgenciesBlocked(
        details?.requestableTransitAgencies,
        details?.transitPasses
      ),
      commuterWorksiteArchived: commuterWorksite?.archived,
      worksiteLabel: commuterWorksite ? 'Change Worksite' : 'Add to Worksite',
      employee: basicCommuterInfo,
      activeTab,
    })
  }

  if (hasError) {
    return (
      <SegmentsDrawer
        title='Commuter Details'
        open={open}
        onCloseModal={onCloseModal}
      >
        <Loading isCard={false} width={200} height={200} />
      </SegmentsDrawer>
    )
  }

  return (
    <>
    <SegmentsDrawer
      title='Commuter Details'
      open={open}
      onCloseModal={onCloseModal}
    >
      <BoxContainer
        backgroundImageType='gradient'
        style={{ overflow: 'visible' }}
      >
        <FlexContainer
          flexDirection='column'
          marginBottom='1.5rem'
          width='100%'
        >
          <FlexContainer alignItems='center' width='100%'>
          <Avatar name={basicCommuterInfo.name} size='xlarge' />
          <FlexContainer
            flexDirection='column'
            marginLeft='1.5rem'
            flexGrow={1}
            width='90%'
          >
            <FlexContainer
              alignItems='center'
              justifyContent='space-between'
              width='100%'
            >
              <FlexContainer alignItems='center'>
                <Text variant='h5' marginRight='0.75rem'>
                  {basicCommuterInfo.name}
                </Text>
                <FlexContainer alignItems='center'>
                  {archivedEmp ? (
                    <FlexContainer>
                      <AlertIcon color={palette.text.primary} />
                      <Text
                        variant='action3'
                        marginTop='0.125rem'
                        marginLeft='0.25rem'
                      >
                        {isBlockedEmp ? 'Blocked' : 'Archived'} on{' '}
                        {moment(archivedEmp.archiveDate).format(
                          'MMMM DD, YYYY'
                        )}
                      </Text>
                    </FlexContainer>
                  ) : (
                    <FlexContainer>
                      <ColorsPill
                        color={
                          statusChipAttributes.color as
                            | 'gray'
                            | 'yellow'
                            | 'red'
                        }
                        text={statusChipAttributes.text}
                      />
                    </FlexContainer>
                  )}
                </FlexContainer>
              </FlexContainer>
              {!isBlockedEmp && (
                <Dropdown
                  variant='textual'
                  svgDropdown={true}
                  icon={<OverflowIcon />}
                  active={null}
                  items={getSelectItems()}
                  itemCallback={menuCallback}
                  width={pxToRem(200)}
                  top='2.75rem'
                  right={0}
                />
              )}
            </FlexContainer>
            <HeaderContainer alignItems='center'>
              <Text variant='action4' hideOverflow>
                ID: #
                {basicCommuterInfo.employeeCorporateId
                  ? basicCommuterInfo.employeeCorporateId
                  : 'No ID'}
              </Text>
            </HeaderContainer>
          </FlexContainer>
          </FlexContainer>
            <CommuterBasicInfo
              employeeId={employeeId}
              email={commuterEmail}
              worksiteAlias={commuterWorksite?.alias}
              address={commuterAddress}
              fleetCards={fleetCards}
            />
            <CommuterBalancesBenefits
              programData={myCommuterDetails[employeeId]?.programData}
              transitPasses={myCommuterDetails[employeeId]?.transitPasses}
              segmentsEnrolled={myCommuterDetails[employeeId]?.segmentsEnrolled}
              tagsEnrolled={myCommuterDetails[employeeId]?.tagsEnrolled}
              qtfBalanceData={myCommuterDetails[employeeId]?.qtfBalanceData}
              qtfPrograms={qtfPrograms}
              grhPrograms={grhPrograms}
            />
            <CommuterGroupsEngagement
              currentDate={currentDate}
              employeeId={employeeId}
              segmentsEnrolled={myCommuterDetails[employeeId]?.segmentsEnrolled}
              segmentsById={segmentsMap}
              incentiveData={myCommuterDetails[employeeId]?.incentiveData}
              competitionData={myCommuterDetails[employeeId]?.competitionData}
              oneTimeRewards={allOneTimeRewards}
            />
        </FlexContainer>
      </BoxContainer>
      {activeTab < 0 ? (
        <SecondaryButton onClick={() => setActiveTab(DEFAULT_TAB)}>
          Cancel
        </SecondaryButton>
      ) : (
        <FlexContainer width='100%'>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            tabs={[
              { label: 'Allocations', value: ALLOCATIONS_TAB },
              { label: 'Transactions', value: TRANSACTIONS_TAB },
              { label: 'Reimbursements', value: REIMBURSEMENTS_TAB },
              { label: 'Support', value: SUPPORT_TAB },
              { label: 'Tags', value: TAGS_TAB },
              { label: 'Trips', value: TRIPS_TAB },
            ]}
            style={{ width: '100%' }}
          />
        </FlexContainer>
      )}
      <BodyContainer>
        {activeTab === ALLOCATIONS_TAB && (
          <CommuterAllocations
            allocation={upcomingAllocations?.[employeeId]}
            qtfBalanceData={myCommuterDetails[employeeId]?.qtfBalanceData}
            benefitHistory={myCommuterDetails[employeeId]?.benefitHistory}
          />
        )}
        {activeTab === -2 && (
          <MyCommuterChangeWorksite
            commuterDetails={myCommuterDetails[employeeId]}
            selectedWorksiteId={selectedWorksiteId}
            onWorksiteIdChange={(worksiteId) =>
              setSelectedWorksiteId(worksiteId)
            }
            onCancel={() => setActiveTab(DEFAULT_TAB)}
            onChangeWorksite={() =>
              handleWorksiteChange(employeeId, selectedWorksiteId)
            }
            isChangeWorksiteDisabled={isFinchEmp || !selectedWorksiteId}
            changeWorksiteText={
              commuterWorksite ? 'Change Worksite' : 'Add to Worksite'
            }
          />
        )}
        {activeTab === -3 && (
          <MyCommuterInvite
            employeeId={employeeId}
            onCancel={() => setActiveTab(DEFAULT_TAB)}
            onInvite={handleInviteEmployee}
          />
        )}
        {activeTab === -4 && (
          <CommuterAllocationAdjustment
            allocation={upcomingAllocations[employeeId]}
            onCancel={() => setActiveTab(DEFAULT_TAB)}
          />
        )}
        {activeTab === -5 && (
          <MyCommuterGiveOTR
            otrAmount={otrAmount}
            onOTRAmountChange={(otrAmount) => setOtrAmount(otrAmount)}
            onOTRMemoChange={(otrMemo) => setOtrMemo(otrMemo)}
            onCancel={() => setActiveTab(DEFAULT_TAB)}
            onGiveOTR={() => handleGiveOTRClick()}
            isGiveOTRDisabled={otrAmount === 0 || otrMemo === ''}
          />
        )}
        {activeTab === -1 && (
          <MyCommuterArchive
            commuterDetails={myCommuterDetails[employeeId]}
            onCancel={() => setActiveTab(DEFAULT_TAB)}
            onArchive={() => handleArchiveClick(employeeId)}
            isArchiveDisabled={isFinchEmp}
          />
        )}
        {activeTab === -6 && (
          <MyCommuterManageTags
            employeeId={employeeId}
            onCancel={() => setActiveTab(manageTagsReturnTab)}
            onSaveComplete={handleTagsSaveComplete}
          />
        )}
        {activeTab === TRIPS_TAB && (
          <MyCommuterDetailsTrips
            currentDate={currentDate}
            commuterDetails={myCommuterDetails[employeeId]}
            onIncreaseClick={() => handleDateChange(true, currentDate)}
            onDecreaseClick={() => handleDateChange(false, currentDate)}
          />
        )}
        {activeTab === TRANSACTIONS_TAB && (
          <MyCommuterDetailsTransactions
            currentDate={currentDate}
            commuterTransactions={myCommuterTransactions[employeeId]}
            onIncreaseClick={() => handleDateChange(true, currentDate)}
            onDecreaseClick={() => handleDateChange(false, currentDate)}
          />
        )}
        {activeTab === REIMBURSEMENTS_TAB && (
          <CommuterReimbursements
            receipts={
              employeeSubmissionsEmployeeId === employeeId
                ? employeeSubmissions
                : null
            }
            loading={employeeSubmissionsLoading}
            error={employeeSubmissionsError}
          />
        )}
        {activeTab === SUPPORT_TAB && <CommuterSupport />}
        {activeTab === TAGS_TAB && (
          <MyCommuterDetailsTags
            commuterDetails={myCommuterDetails[employeeId]}
            onManageTags={() => openManageTags(TAGS_TAB)}
          />
        )}
      </BodyContainer>
    </SegmentsDrawer>
    <RequestTransitCardModal
      open={isRequestTransitModalOpen}
      commuterName={basicCommuterInfo.name}
      commuterEmail={commuterEmail}
      worksiteName={commuterWorksite?.alias}
      agencies={myCommuterDetails[employeeId]?.requestableTransitAgencies}
      transitPasses={myCommuterDetails[employeeId]?.transitPasses}
      isSubmitting={requestTransitCardStatus === 'loading'}
      onClose={() => {
        setIsRequestTransitModalOpen(false)
        dispatch(employeeActions.toggleRequestTransitCardStatusOff())
      }}
      onRequest={handleRequestTransitCard}
    />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  CommutersInfoDrawer.displayName = 'CommutersInfoDrawer'
}

export default CommutersInfoDrawer
